import React, { useEffect } from 'react';
import { Navigate, Route, Routes, Outlet, useLocation,useParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { HotKeys } from 'react-hotkeys';

// Stylesheets
import './App.scss';

// Redux
import { showAlert } from './app/slices/alertSlice';
import { selectModal } from './app/slices/modalSlice';
import { fetchCurrentSubscription, fetchStudio, selectStudio } from './app/slices/studioSlice';
import { selectIsAuthenticated, selectUser, selectUserStudio, verifyAuth, selectAuthLoading } from './app/slices/authSlice';
import { fetchProjects, selectProjectsStatus, updateProjectsStatus } from './app/slices/projectsSlice';

// Features
import Home from './features/Home/Home';
import Project from './features/Project/Project';
import Projects from './features/Projects/Projects';
import Packages from './features/Packages/Packages';
import LoginModal from './features/Login/Login';
import Storage from './features/Storage/Storage';
import Galleries from './features/Galleries/Galleries';
import SelectionPIN from './features/Selection/SelectionPIN';
import Selection from './features/Selection/Selection';
import Teams from './features/Teams/Teams';
import Onboarding from './features/Onboarding/v2/Onboarding';
import AdminPanel from './features/AdminPanel/AdminPanel';
import DeveloperTools from './features/AdminPanel/DeveloperTools';
import Notifications from './features/Notifications/Notifications';
import InvitationPage from './features/Invitation/InvitationPage';
import InvitationPreview from './features/Invitation/InvitationPreview';
import PortfolioWebsite from './features/Website/Website';
import Settings from './features/Settings/Settings';
import BillingHistory from './features/BillingHistory/BillingHistory';
import SmartGallery from './features/SmartGallery/SmartGallery';
import SmartGalleryDownloadPIN from './features/SmartGallery/SmartGalleryDownloadPIN';
import UserProfile from './features/AdminPanel/UserProfile/UserProfile';
import StudioProfile from './features/AdminPanel/StudioProfile/StudioProfile';
import DownloadApp from './features/DownloadApp/DownloadApp';
import Booking from './features/Booking/Booking';

// Components
import Alert from './components/Alert/Alert';
import Header from './components/Header/Header';
import Sidebar from './components/Sidebar/Sidebar';
import SearchResults from './components/Search/SearchResults';
import Subscription from './components/Subscription/Subscription';
import UploadProgress from './components/UploadProgress/UploadProgress';
import Loading, { LoadingLight } from './components/Loading/Loading';
import LoadingScreen from './components/Loading/LoadingScreen';
import CommingSoon from './components/CommingSoon/CommingSoon';
import NotFound from './components/NotFound/NotFound';
import SupportIcon from './components/Modal/SupportIcon/FlowPilot';
import StudioAccessGate from './components/AccessGate/StudioAccessGate';

// Modal Components
import TrialStatusModal from './components/Modal/TrialEnds/TrialEnds';
import UpgradeModal from './components/Subscription/UpgradeModal';

// Hooks
import { useShortcutsConfig } from './hooks/shortcutsConfig';

// Utils
import { setUserType } from './analytics/utils';
import { isPublicPage, isLightModePage } from './utils/publicPages';
import { getCurrentSubscription } from './firebase/functions/subscription';
import { welcomeConsole } from './utils/welcomeConsole';
import { hideLoading, showLoading } from './app/slices/loadingSlice';


welcomeConsole();

// Wrapper for authenticated routes
const AuthWrapper = ({ isAuthenticated }) => {
  const location = useLocation();
  if (!isAuthenticated) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }
  return <Outlet />;
};

// Redirect for legacy share links
const ShareRedirect = () => {
  const { studioName, projectId, collectionId } = useParams();
  const targetPath = `/${studioName}/smart-gallery/${projectId}${collectionId ? `/${collectionId}` : ''}`;
  return <Navigate to={targetPath} replace />;
};


// APP
export default function App() {
  // Canonical domain guard (backup to the <head> inline redirect in
  // public/index.html): legacy *.web.app / *.firebaseapp.com links land on
  // https://app.fotoflow.co with path + query preserved. Gallery routes are
  // handled server-side with 301 by serveGallery; this covers SPA routes.
  useEffect(() => {
    try {
      const host = window.location.hostname.toLowerCase();
      const isLegacy =
        host === 'fotoflow-studio.web.app' ||
        host === 'fotoflow-studio.firebaseapp.com' ||
        host === 'fotoflow-cloud.web.app' ||
        host === 'fotoflow-cloud.firebaseapp.com' ||
        host.endsWith('.web.app') ||
        host.endsWith('.firebaseapp.com');
      if (isLegacy) {
        const target =
          'https://app.fotoflow.co' +
          window.location.pathname +
          window.location.search +
          window.location.hash;
        window.location.replace(target);
      }
    } catch { /* ignore */ }
  }, []);

  const dispatch = useDispatch();
  const location = useLocation();
  const user = useSelector(selectUser);

  const authLoading = useSelector(selectAuthLoading);

  const isLoading = useSelector(selectProjectsStatus);
  const defaultStudio = useSelector(selectUserStudio)
  const studio = useSelector(selectStudio);

  const modals = useSelector(selectModal);
  const currentDomain = defaultStudio?.domain ?? 'guest'; 
  const isAuthenticated = useSelector(selectIsAuthenticated);
  const { keyMap, handlers } = useShortcutsConfig();

  useEffect(() => {
    dispatch(verifyAuth());
  }, [dispatch]);


  useEffect(() => {
    if(isAuthenticated && user!=='no-studio-found'){
      console.log(`%cUser authenticated as ${user.email} | Studio: ${defaultStudio?.domain}`, `color: green`);
      setUserType('Photographer');
    }
  }, [isAuthenticated, user, defaultStudio]);

  // ON Render
  useEffect(() => {
    if (isAuthenticated && currentDomain !== 'guest' && !isPublicPage(location.pathname)) {
      if (isLoading === 'login' || isLoading === 'failed') {
        dispatch(showLoading({ context: ` Loading App ..`, subcontext: ` ` }));
        // Fetching data for studio
        console.log(`%cFetching data for ${currentDomain}...`, `color: gray`);
        dispatch(fetchProjects({ currentDomain }))
          .catch((err) => {
            dispatch(showAlert({ type: 'error', message: 'Check internet connection' }));
          });

        dispatch(fetchStudio({ currentDomain })).then((a) => {
          dispatch(fetchCurrentSubscription({ currentDomain }))
            .catch((err) => {
              console.error(err);
              dispatch(hideLoading());
            });
        })
          .then(() => {
            dispatch(hideLoading());
          })
          .catch((err) => {
            console.error(err);
            dispatch(hideLoading());
          });
      }
    }
  }, [dispatch, currentDomain, isAuthenticated, location.pathname, isLoading]);

  useEffect(() =>{
    if (studio?.trialEndDate) {
      const trialDaysLeft = Math.ceil((new Date(studio.trialEndDate).getTime() - new Date().getTime()) / (1000 * 60 * 60 * 24));
      console.log(`Trial ends on ${studio?.trialEndDate}, in ${trialDaysLeft} days`)
    }
  },[studio?.trialEndDate])

  useEffect(() => {
    const modalStates = Object.values(modals);
    if (modalStates.some(state => state)) {
      window.scrollTo(0, 0);
      document.body.style.overflow = 'hidden';
    } 
    else {
      document.body.style.overflow = 'auto';
    }
  }, [modals]);

  useEffect(() => {
    if(defaultStudio?.domain){
        getCurrentSubscription(defaultStudio.domain)
    }
  }, [defaultStudio]);

  if (authLoading) {
    return <LoadingLight />;
  }
  // RENDER
  return (
    <div className={`App ${isLightModePage(location.pathname) ? 'light-mode-page' : ''}`}>
      <LoadingScreen />
      <HotKeys keyMap={keyMap} handlers={handlers} className='app-wrap'>
      {/* <SupportIcon userId={defaultStudio?.domain}/> */}
      {isAuthenticated && (!isPublicPage(location.pathname)) && (
        <>
          <Header />
          <Sidebar />
          <Alert />
          <UploadProgress/>
          <UpgradeModal/>
          <TrialStatusModal/>

          {/* <SupportIcon/> */}
        </>
      )}
      {
        isLoading !== 'succeeded' && isAuthenticated && user !== 'no-studio-found' && (!isPublicPage(location.pathname)) ?
          (
            isLoading !== 'login' ? <Loading /> : <LoadingLight />
          ) : 
          (
            <Routes>
              {/* Public Routes */}
              <Route path="/login" element={<LoginModal />} />
              <Route path="/onboarding" element={<Onboarding />} />
              <Route path="/download-app" element={<DownloadApp />} />
              <Route path="/:studioName/booking" element={<Booking />} />
              <Route path="/:studioName/smart-gallery/:projectId/:collectionId?" element={<SmartGallery/>}/>
              <Route path="/:studioName/smart-gallery/:projectId/download/pin" element={<SmartGalleryDownloadPIN/>}/>
              <Route path="/:studioName/share/:projectId/:collectionId?" element={<ShareRedirect/>}/>
              <Route path="/:studioName/selection/:projectId/pin" element={<SelectionPIN/>}/>
              <Route path="/:studioName/selection/:projectId/:collectionId?" element={<Selection/>}/>
              <Route path="/:studioName/invitation/:projectId/:eventId?" element={<InvitationPreview/>}/>

              {/* Authenticated Routes */}
              <Route element={<AuthWrapper isAuthenticated={isAuthenticated} />}>
                <Route path="/" element={<Navigate to={`/${defaultStudio.domain}/home`} replace />} />
                <Route path="/search" element={<SearchResults />} />

                {/* Studio Workspace Routes - Protected by StudioAccessGate */}
                <Route element={<StudioAccessGate />}>
                  <Route path="/:studioName" element={<Navigate to={`/${defaultStudio.domain}/home`} replace />} />
                  <Route path="/:studioName/home" element={<Home />} />
                  <Route path="/:studioName/project/:id" element={<Project />} />
                  <Route path="/:studioName/gallery/:id/:collectionId?" element={<Galleries />} />
                  <Route path="/:studioName/portfolio-editor" element={<PortfolioWebsite />} />
                  <Route path="/:studioName/invitation-creator/:projectId" element={<InvitationPage/>} />
                  <Route path="/:studioName/projects" element={<Projects />} />
                  <Route path="/:studioName/packages" element={<Packages />} />
                  <Route path="/:studioName/notifications" element={<Notifications />} />
                  <Route path="/:studioName/storage" element={<Storage />} />
                  <Route path="/:studioName/store" element={<CommingSoon title={'Store'}/>} />
                  <Route path="/:studioName/calendar" element={<CommingSoon title={'Calendar'}/>} />
                  <Route path="/:studioName/invoices" element={<CommingSoon title={'Financials'}/>} />
                  <Route path="/:studioName/accounts" element={<CommingSoon title={'Accounts'}/>} />
                  <Route path="/:studioName/team" element={<Teams />} />
                </Route>

                {/* Subscription / billing / settings routes - EXCLUDED from access block so suspended/inactive users can renew & view billing (same as Admin/Tools) */}
                <Route element={<StudioAccessGate allowSubscriptionPage={true} />}>
                  <Route path="/:studioName/subscription" element={<Subscription />} />
                  <Route path="/:studioName/subscription/history" element={<BillingHistory />} />
                  <Route path="/:studioName/settings" element={<Settings/>} />
                </Route>

                {/* Admin Routes - EXCLUDED from StudioAccessGate */}
                <Route path="/admin" element={<AdminPanel />} />
                <Route path="/admin/:page" element={<AdminPanel />} />
                <Route path="/admin/user/:userId" element={<UserProfile />} />
                <Route path="/admin/studio/:studioName" element={<StudioProfile />} />
                <Route path="/tools" element={<DeveloperTools />} />
              </Route>

              <Route path="*" element={<NotFound />} />
            </Routes>
          )}

{!isLightModePage(location.pathname) && location.pathname !== '/login' && <div className="footer">
            {/* Made in Kochi by Photographers |  */}
            <div className='copyright-symbol'>©</div>  
            <a href="https://www.masanory.com" target="_blank" rel="noopener noreferrer"><span>Fotoflow</span> 2025</a>
          </div>}
          </HotKeys>
          
    </div>
    
  );
}